import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { MsalService } from "@azure/msal-angular";
import { RedirectRequest } from "@azure/msal-browser";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import {
	ErrorState,
	ErrorStateService,
} from "src/app/_core/providers/error-state.service";

/**
 * Página de error fatal (/error). Renderiza FUERA del layout autenticado
 * (ErrorLayoutComponent, sin guards) para que siempre pinte, incluso con la
 * sesión rota. Muestra el código real vía ErrorStateService y ofrece salidas:
 * reintentar sesión (re-login MSAL), ir al inicio o cerrar sesión.
 */
@Component({
	selector: "app-error",
	templateUrl: "./error.component.html",
	styleUrls: ["./error.component.scss"],
	standalone: false,
})
export class ErrorComponent implements OnInit, OnDestroy {
	errorState: ErrorState | null = null;
	readonly defaultErrorCode = "500";
	private readonly _destroying$ = new Subject<void>();

	constructor(
		private readonly errorStateService: ErrorStateService,
		private readonly msalService: MsalService,
		private readonly router: Router
	) {}

	ngOnInit(): void {
		this.errorStateService.error$
			.pipe(takeUntil(this._destroying$))
			.subscribe((error) => {
				this.errorState = error;
			});
	}

	ngOnDestroy(): void {
		this._destroying$.next();
		this._destroying$.complete();
	}

	get code(): string {
		return this.errorState?.code || this.defaultErrorCode;
	}

	get icon(): string {
		switch (this.code) {
			case "401":
				return "lock";
			case "403":
				return "block";
			case "404":
				return "search_off";
			case "0":
				return "cloud_off";
			default:
				return "report_problem";
		}
	}

	get titleKey(): string {
		return `shared.errorPage.title${this.knownCode}`;
	}

	get descriptionKey(): string {
		return `shared.errorPage.desc${this.knownCode}`;
	}

	private get knownCode(): string {
		return ["401", "403", "404", "500", "0"].includes(this.code)
			? this.code
			: "Default";
	}

	/** Re-login MSAL por redirect: resuelve la mayoría de sesiones caducadas al instante (SSO). */
	retrySession(): void {
		this.errorStateService.clearError();
		this.msalService.loginRedirect({
			scopes: ["user.read", "contacts.read"],
			redirectStartPage: "/",
		} as RedirectRequest);
	}

	goHome(): void {
		this.errorStateService.clearError();
		this.router.navigate(["/home"]);
	}

	logout(): void {
		this.errorStateService.clearError();
		this.msalService.logoutRedirect();
	}
}
